"use client";

import React from "react";
import { Calendar, User, ArrowLeft, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";

declare const wvcClient: any;

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse">
        <div className="mb-8 flex flex-col items-center space-y-4 text-center">
          <div className="h-12 w-3/4 rounded-lg bg-muted"></div>
          <div className="h-12 w-1/2 rounded-lg bg-muted"></div>
          <div className="mt-4 flex gap-4">
            <div className="h-4 w-24 rounded bg-muted"></div>
            <div className="h-4 w-24 rounded bg-muted"></div>
          </div>
        </div>
        <div className="mb-12 h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
          <div className="h-4 w-4/6 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center justify-center rounded-xl border border-destructive/20 bg-destructive/10 p-12 text-center">
        <CircleAlert className="mb-4 size-12 text-destructive" />
        <h2 className="mb-2 font-default text-xl font-semibold text-foreground">Failed to load post</h2>
        <p className="mb-6 text-muted-foreground">{error}</p>
        <Button variant="outline" asChild>
          <Link to="/HomePage">Return to Home</Link>
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center shadow-md">
        <h2 className="mb-2 font-default text-2xl font-semibold text-foreground">Post Not Found</h2>
        <p className="mb-6 text-muted-foreground">The article you are looking for does not exist or has been removed.</p>
        <Button asChild className="bg-secondary text-secondary-foreground hover:opacity-90 rounded-lg shadow-md">
          <Link to="/HomePage">
            <ArrowLeft className="mr-2 size-4" />
            Back to Home
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      {posts.map((post, i) => (
        <article key={post.id} data-index={i} className="flex flex-col">
          <header className="mb-10 text-center">
            <h1 className="mb-6 font-serif text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground md:text-base font-default">
              {post.date && (
                <div className="flex items-center">
                  <Calendar className="mr-2 size-4 text-secondary" />
                  <time dateTime={post.date}>{post.date}</time>
                </div>
              )}
              {post.author && (
                <div className="flex items-center">
                  <User className="mr-2 size-4 text-secondary" />
                  <span>{post.author.name}</span>
                </div>
              )}
            </div>
          </header>

          {post.featuredImage && (
            <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-auto w-full object-cover"
                loading="eager"
              />
            </div>
          )}

          <div 
            className="prose prose-lg prose-invert mx-auto mb-16 max-w-none font-default leading-relaxed text-foreground/90 prose-headings:font-serif prose-headings:font-bold prose-headings:text-foreground prose-a:text-secondary prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />

          <Separator className="mb-12 bg-border" />

          {post.author && (
            <Card className="border-border bg-card/50 shadow-md rounded-lg">
              <CardContent className="flex flex-col items-center gap-6 p-8 sm:flex-row sm:items-start">
                <Avatar className="size-20 border-2 border-primary sm:size-24">
                  <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                  <AvatarFallback className="font-serif text-2xl bg-primary text-primary-foreground">{post.author.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="text-center sm:text-left">
                  <h3 className="mb-2 font-serif text-xl font-bold text-foreground">
                    Written by {post.author.name}
                  </h3>
                  {post.author.bio ? (
                    <p className="text-muted-foreground leading-relaxed font-default">
                      {post.author.bio}
                    </p>
                  ) : (
                    <p className="text-muted-foreground leading-relaxed font-default">
                      Coastal lifestyle enthusiast and contributor at SurfPup. Passionate about sharing beach adventures and premium dog gear insights.
                    </p>
                  )}
                  {post.author.link && (
                    <Button variant="link" className="mt-2 h-auto p-0 text-secondary font-semibold" asChild>
                      <Link to={post.author.link}>View all posts by {post.author.name} →</Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </article>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="221682" id="single-post-template-section" className="bg-background text-foreground px-4 py-16 md:px-8 lg:py-24">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}